import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Send, MapPin, CheckCircle2, Loader2, Copy, RefreshCw, Link2Off } from 'lucide-react';
import patientLocationService, { TERMINAL_SHARE_STATUSES } from '../../services/patientLocationService';

const POLL_INTERVAL_MS = 4000;
const RESEND_COOLDOWN_MS = 30000;
// Above this a GPS fix is too coarse to trust as an exact doorstep
const LOW_ACCURACY_M = 100;

const STEPS = [
  { key: 'CREATED', label: 'Link sent' },
  { key: 'OPENED', label: 'Patient opened link' },
  { key: 'CAPTURED', label: 'Location received' },
];

const stepIndex = (status) => Math.max(0, STEPS.findIndex((s) => s.key === status));

const formatCoord = (n) => (n == null ? '' : Number(n).toFixed(6));

// Asks the patient (who is on the phone with the agent) to share their location via an SMS / WhatsApp link,
// then polls the backend until the coordinates arrive. The agent confirms with "Use as pickup / drop-off" —
// nothing is written to the form until they click it, so the entered locations are never overwritten silently.
const PatientLocationSharePopup = ({
  target = 'pickup', // 'pickup' | 'dropoff'
  phone,
  patientName,
  bookingId,
  existingShareId,
  onShareChange,
  onUseLocation,
  onClose,
}) => {
  const targetLabel = target === 'dropoff' ? 'drop-off' : 'pickup';

  const [share, setShare] = useState(null);
  const [devLink, setDevLink] = useState(null);
  const [sendWhatsApp, setSendWhatsApp] = useState(false);
  const [sending, setSending] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [lastSentAt, setLastSentAt] = useState(null);
  const [, forceTick] = useState(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const updateShare = useCallback((next) => {
    setShare(next);
    onShareChange?.(next?.shareId || null);
  }, [onShareChange]);

  const refresh = useCallback(async (shareId) => {
    try {
      const result = await patientLocationService.getLocationShare(shareId);
      if (mountedRef.current && result?.share) setShare(result.share);
    } catch (err) {
      // 404 = the draft was cleaned up (unattached after 1 hour) — treat as expired rather than erroring forever
      if (mountedRef.current && /not found|expired/i.test(err?.message || '')) {
        setShare((prev) => (prev ? { ...prev, status: 'EXPIRED' } : prev));
      }
    }
  }, []);

  // Reopening the popup for a link that was already sent resumes it instead of starting over
  useEffect(() => {
    if (existingShareId) refresh(existingShareId);
  }, [existingShareId, refresh]);

  const shareId = share?.shareId;
  const status = share?.status;
  const polling = Boolean(shareId) && !TERMINAL_SHARE_STATUSES.includes(status);

  useEffect(() => {
    if (!polling) return undefined;
    const id = setInterval(() => refresh(shareId), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [polling, shareId, refresh]);

  // Re-render once a second while the resend cooldown is running
  useEffect(() => {
    if (!lastSentAt || Date.now() - lastSentAt >= RESEND_COOLDOWN_MS) return undefined;
    const id = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [lastSentAt]);

  const cooldownLeftSec = lastSentAt
    ? Math.max(0, Math.ceil((RESEND_COOLDOWN_MS - (Date.now() - lastSentAt)) / 1000))
    : 0;

  const channels = sendWhatsApp ? ['SMS', 'WHATSAPP'] : ['SMS'];

  const summarise = (delivery) => {
    const entries = Object.entries(delivery || {});
    const failed = entries.filter(([, r]) => !r.success).map(([c]) => c);
    if (entries.length && failed.length === entries.length) return 'The link could not be delivered. Share it manually or try again.';
    if (failed.length) return `Sent, but ${failed.join(', ')} failed.`;
    return '';
  };

  const handleSend = async () => {
    setError('');
    setNotice('');
    if (!phone?.trim()) {
      setError('Enter the patient\'s phone number (Phone 1) in the form first.');
      return;
    }
    setSending(true);
    try {
      const result = await patientLocationService.createLocationShare({
        phone: phone.trim(),
        bookingId: bookingId || undefined,
        patientName: patientName?.trim() || undefined,
        channels,
      });
      if (!mountedRef.current) return;
      updateShare(result.share);
      setDevLink(result.devLink || null);
      setLastSentAt(Date.now());
      setNotice(summarise(result.delivery));
    } catch (err) {
      if (mountedRef.current) setError(err.message || 'Failed to send the location link');
    } finally {
      if (mountedRef.current) setSending(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setNotice('');
    setSending(true);
    try {
      const result = await patientLocationService.resendLocationShare(shareId, { channels });
      if (!mountedRef.current) return;
      setShare(result.share);
      setDevLink(result.devLink || devLink);
      setLastSentAt(Date.now());
      setNotice(summarise(result.delivery) || 'Link sent again.');
    } catch (err) {
      if (mountedRef.current) setError(err.message || 'Failed to resend the link');
    } finally {
      if (mountedRef.current) setSending(false);
    }
  };

  const handleRevoke = async () => {
    setError('');
    try {
      const result = await patientLocationService.revokeLocationShare(shareId);
      if (mountedRef.current) {
        setShare(result.share);
        setNotice('Link cancelled. You can send a new one.');
      }
    } catch (err) {
      if (mountedRef.current) setError(err.message || 'Failed to cancel the link');
    }
  };

  const handleUse = async () => {
    if (!share?.location) return;
    setApplying(true);
    setError('');
    try {
      await onUseLocation?.(share.location);
      onClose?.();
    } catch (err) {
      if (mountedRef.current) setError(err.message || `Could not set the ${targetLabel} location`);
    } finally {
      if (mountedRef.current) setApplying(false);
    }
  };

  const handleCopy = () => {
    try {
      navigator.clipboard?.writeText(devLink);
      setNotice('Link copied.');
    } catch (_) { /* clipboard unavailable — link is still visible */ }
  };

  const canUse = Boolean(onUseLocation);
  const captured = status === 'CAPTURED' && share?.location;
  const dead = TERMINAL_SHARE_STATUSES.includes(status);
  const lowAccuracy = captured && share.location.accuracyM != null && share.location.accuracyM > LOW_ACCURACY_M;

  // Rendered into document.body (above the trip modals' own overlays) and clicks are stopped here so closing this
  // popup never bubbles through React to a parent modal's backdrop and closes that too.
  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => { e.stopPropagation(); onClose?.(); }}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Request patient location"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-600" />
            <h2 className="text-base font-semibold text-gray-900">Request patient location</h2>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <p className="text-sm text-gray-600">
            Send the patient a link. When they open it and tap <b>Allow</b>, their location appears here
            {canUse ? <> and you can use it as the {targetLabel} location.</> : <> and on the trip map as a separate marker. The pickup location is not changed.</>}
          </p>

          <div className="rounded-lg border border-gray-200 px-3 py-2 text-sm">
            <span className="text-xs font-semibold text-gray-500 block">Sending to</span>
            <span className="font-medium text-gray-900">{phone?.trim() || 'No phone number entered'}</span>
            {patientName?.trim() && <span className="text-gray-500"> · {patientName.trim()}</span>}
          </div>

          {!share && (
            <>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={sendWhatsApp}
                  onChange={(e) => setSendWhatsApp(e.target.checked)}
                  className="rounded border-gray-300"
                />
                Also send on WhatsApp
              </label>
              <button
                type="button"
                onClick={handleSend}
                disabled={sending}
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sending ? 'Sending…' : 'Send location link'}
              </button>
            </>
          )}

          {share && (
            <>
              {/* Progress: Sent -> Opened -> Received */}
              <ol className="space-y-2">
                {STEPS.map((step, i) => {
                  const reached = !dead && stepIndex(status) >= i;
                  const current = !dead && stepIndex(status) === i && status !== 'CAPTURED';
                  return (
                    <li key={step.key} className="flex items-center gap-2 text-sm">
                      {reached ? (
                        current ? <Loader2 className="w-4 h-4 text-blue-500 animate-spin" /> : <CheckCircle2 className="w-4 h-4 text-green-600" />
                      ) : (
                        <span className="w-4 h-4 rounded-full border border-gray-300 inline-block" />
                      )}
                      <span className={reached ? 'text-gray-900 font-medium' : 'text-gray-400'}>{step.label}</span>
                    </li>
                  );
                })}
              </ol>

              {status === 'CREATED' && <p className="text-xs text-gray-500">Waiting for the patient to open the link…</p>}
              {status === 'OPENED' && <p className="text-xs text-gray-500">Patient opened the link — ask them to tap “Allow” on the location prompt.</p>}

              {captured && (
                <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-3 text-sm">
                  <div className="font-semibold text-green-800">Location received</div>
                  <div className="text-green-900 font-mono text-xs mt-1">
                    {formatCoord(share.location.latitude)}, {formatCoord(share.location.longitude)}
                  </div>
                  {share.location.accuracyM != null && (
                    <div className="text-xs text-green-800 mt-0.5">Accurate to about {Math.round(share.location.accuracyM)} m</div>
                  )}
                  {lowAccuracy && (
                    <div className="text-xs text-amber-700 mt-1">
                      Low accuracy — ask the patient to tap “Share again” near a window or outdoors.
                    </div>
                  )}
                </div>
              )}

              {dead && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 flex items-start gap-2">
                  <Link2Off className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{status === 'REVOKED' ? 'This link was cancelled.' : 'This link has expired.'} Send a new one.</span>
                </div>
              )}

              {captured && canUse && (
                <button
                  type="button"
                  onClick={handleUse}
                  disabled={applying}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                  {applying ? 'Setting location…' : `Use as ${targetLabel} location`}
                </button>
              )}

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => refresh(shareId)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Refresh
                </button>
                {!dead && (
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={sending || cooldownLeftSec > 0}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <Send className="w-3.5 h-3.5" />
                    {cooldownLeftSec > 0 ? `Resend in ${cooldownLeftSec}s` : 'Resend link'}
                  </button>
                )}
                {!dead && (
                  <button
                    type="button"
                    onClick={handleRevoke}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                  >
                    Cancel link
                  </button>
                )}
                {dead && (
                  <button
                    type="button"
                    onClick={() => { updateShare(null); setDevLink(null); setNotice(''); setError(''); }}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                  >
                    <Send className="w-3.5 h-3.5" /> Send a new link
                  </button>
                )}
              </div>
            </>
          )}

          {/* The server returns the link so staff can copy and share it manually when SMS / WhatsApp is unavailable */}
          {devLink && (
            <div className="rounded-lg border border-dashed border-blue-300 bg-blue-50 px-3 py-2">
              <div className="text-[11px] font-semibold text-blue-800 mb-1">Patient link (copy and share it if the message does not arrive)</div>
              <a href={devLink} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-700 break-all underline">
                {devLink}
              </a>
              <button type="button" onClick={handleCopy} className="ml-2 inline-flex items-center gap-1 text-xs text-blue-700 hover:underline">
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
          )}

          {notice && <p className="text-xs text-gray-600" role="status">{notice}</p>}
          {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PatientLocationSharePopup;
